import { useEffect } from 'react';

import i18n from '~ai-tutor/lib/i18n';

const USERCENTRICS_SCRIPT_ID = 'usercentrics-cmp';
const USERCENTRICS_SRC = 'https://web.cmp.usercentrics.eu/ui/loader.js';
const USERCENTRICS_SETTINGS_ID = 'AJOEY0ITHdv7ux';

function applyUsercentricsLanguage(language: string): void {
  // eslint-disable-next-line unicorn-x/prefer-global-this -- UC_UI is attached to window by Usercentrics
  window.UC_UI?.updateLanguage?.(language);
}

function injectUsercentricsScript(): void {
  const script = document.createElement('script');
  script.id = USERCENTRICS_SCRIPT_ID;
  script.src = USERCENTRICS_SRC;
  script.async = true;
  script.dataset.settingsId = USERCENTRICS_SETTINGS_ID;
  script.dataset.language = i18n.language;
  document.head.append(script);
}

/**
 * Loads Usercentrics after hydration. Putting the CMP script in route `head.scripts`
 * causes hydration mismatches: the loader injects preload links into `<head>` before
 * React hydrates, which also leaves `window.UC_UI` unavailable for the footer.
 *
 * Language follows i18n (synced from tutor settings). Initial `data-language` may still
 * be the fallback; `UC_UI.updateLanguage` applies the real language when i18n changes
 * or when the CMP finishes initializing.
 */
export function UsercentricsSetup() {
  useEffect(() => {
    const syncLanguage = (language: string) => {
      applyUsercentricsLanguage(language);
    };

    const onInitialized = () => {
      syncLanguage(i18n.language);
    };

    globalThis.addEventListener('UC_UI_INITIALIZED', onInitialized);
    i18n.on('languageChanged', syncLanguage);

    if (document.querySelector(`#${USERCENTRICS_SCRIPT_ID}`)) {
      // Script already present (e.g. remount); apply current language if CMP is ready
      syncLanguage(i18n.language);
    } else {
      injectUsercentricsScript();
    }

    return () => {
      globalThis.removeEventListener('UC_UI_INITIALIZED', onInitialized);
      i18n.off('languageChanged', syncLanguage);
    };
  }, []);

  return <></>;
}
